<script setup lang="ts">
import { onMounted, computed, ref, watch, markRaw } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import ColumnSelector from '@/components/data-visualization/grids/ColumnSelector.vue'
import type { ColumnConfig } from '@/components/data-visualization/grids/ColumnSelector.vue'
import EnvelopeStatusBadge from '@/components/features/envelopes/EnvelopeStatusBadge.vue'
import EnvelopeSubmittersCell from '@/components/features/envelopes/EnvelopeSubmittersCell.vue'
import EnvelopeClientCell from '@/components/features/envelopes/EnvelopeClientCell.vue'
import EnvelopeCommercialCell from '@/components/features/envelopes/EnvelopeCommercialCell.vue'
import { formatDate } from '@/utils/dateUtils'
import { useEnvelopeStore, type Envelope, type EnvelopeFilters } from '@/stores/useEnvelopeStore'

const envelopeStore = useEnvelopeStore()

const envelopes = ref<Envelope[]>([])
const filters = ref<EnvelopeFilters>({ q: '', status: '' })
const pageSize = ref<number>(50)
const page = ref<number>(1)
const totalEnvelopes = ref<number>(0)
const totalPages = ref<number>(1)

let timeout: ReturnType<typeof setTimeout> | null = null

const allColumns = computed<ColumnConfig[]>(() => [
  { field: 'templateName', headerName: 'Document', visible: true },
  { field: 'company', headerName: 'Client', visible: true },
  { field: 'commercial', headerName: 'Commercial', visible: true },
  { field: 'submitters', headerName: 'Signataires', visible: true },
  { field: 'status', headerName: 'Statut', visible: true },
  { field: 'externalCreatedAt', headerName: 'Envoyé le', visible: true },
  { field: 'completedAt', headerName: 'Signé le', visible: true },
])

const visibleColumnFields = ref<string[]>([])

const gridColumns = computed(() => {
  const visible = new Set(visibleColumnFields.value)
  return [
    {
      field: 'templateName',
      headerName: 'Document',
      cellClass: 'border-0',
      filter: false,
      hide: !visible.has('templateName'),
      valueFormatter: (params: { value: string | null }) => params.value || '(sans nom)',
    },
    {
      field: 'company',
      headerName: 'Client',
      cellClass: 'border-0',
      filter: false,
      hide: !visible.has('company'),
      cellRenderer: markRaw(EnvelopeClientCell),
    },
    {
      field: 'commercial',
      headerName: 'Commercial',
      cellClass: 'border-0',
      filter: false,
      hide: !visible.has('commercial'),
      cellRenderer: markRaw(EnvelopeCommercialCell),
    },
    {
      field: 'submitters',
      headerName: 'Signataires',
      cellClass: 'border-0',
      filter: false,
      hide: !visible.has('submitters'),
      autoHeight: true,
      cellRenderer: markRaw(EnvelopeSubmittersCell),
    },
    {
      field: 'status',
      headerName: 'Statut',
      cellClass: 'border-0',
      filter: false,
      hide: !visible.has('status'),
      cellRenderer: markRaw(EnvelopeStatusBadge),
    },
    {
      field: 'externalCreatedAt',
      headerName: 'Envoyé le',
      cellClass: 'border-0',
      filter: false,
      hide: !visible.has('externalCreatedAt'),
      valueFormatter: (params: { value: string | null }) => (params.value ? formatDate(params.value) : '-'),
    },
    {
      field: 'completedAt',
      headerName: 'Signé le',
      cellClass: 'border-0',
      filter: false,
      hide: !visible.has('completedAt'),
      valueFormatter: (params: { value: string | null }) => (params.value ? formatDate(params.value) : '-'),
    },
  ]
})

watch(
  filters,
  () => {
    page.value = 1
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => {
      fetchEnvelopes()
    }, 400)
  },
  { deep: true },
)

onMounted(() => {
  fetchEnvelopes()
})

async function fetchEnvelopes() {
  const cleanFilters: EnvelopeFilters = {}
  if (filters.value.q) cleanFilters.q = filters.value.q
  if (filters.value.status) cleanFilters.status = filters.value.status

  const offset = (page.value - 1) * pageSize.value
  const result = await envelopeStore.fetchEnvelopes(cleanFilters, pageSize.value, offset)
  if (result) {
    envelopes.value = result.data
    totalEnvelopes.value = result.total
    totalPages.value = Math.max(1, Math.ceil(result.total / pageSize.value))
  }
}

function onColumnsChanged(newVisibleColumns: string[]) {
  visibleColumnFields.value = newVisibleColumns
}

async function onPaginationChanged(newPageSize: number, newPage: number) {
  if (pageSize.value === newPageSize && page.value === newPage) return
  let changed = false
  if (pageSize.value !== newPageSize) {
    pageSize.value = newPageSize
    changed = true
  }
  if (page.value !== newPage) {
    page.value = newPage
    changed = true
  }
  if (changed) await fetchEnvelopes()
}
</script>

<template>
  <div class="p-6">
    <div class="flex items-center justify-between gap-4">
      <Bs2Title title="Tableau de bord des signatures" subtitle="Suivi des enveloppes DocuSeal" />
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body p-4 pt-2">
        <GridVue
          :list="envelopes"
          :cols="gridColumns"
          :get-row-height="() => 60"
          :use-server-side-pagination="true"
          :pagination-page-size="pageSize"
          :pagination-total-rows="totalEnvelopes"
          :current-page="page"
          :total-pages="totalPages"
          @onPaginationChanged="onPaginationChanged"
        >
          <template #title>
            <h2 class="card-title">Signatures en cours et signées</h2>
          </template>
          <template #filter>
            <input
              v-model="filters.q"
              type="text"
              placeholder="Rechercher (document, client, contact, email)..."
              class="input input-sm input-bordered flex-1 min-w-64"
            />
            <select v-model="filters.status" class="select select-sm select-bordered">
              <option value="">Tous les statuts</option>
              <option value="PENDING">En attente</option>
              <option value="COMPLETED">Signé</option>
              <option value="DECLINED">Refusé</option>
              <option value="EXPIRED">Expiré</option>
              <option value="ARCHIVED">Archivé</option>
            </select>
            <ColumnSelector
              :columns="allColumns"
              cookie-name="envelopes-columns-visibility"
              @columns-changed="onColumnsChanged"
            />
          </template>
        </GridVue>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
